import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import BookList from './components/books/BookList';
import BookDetail from './components/books/BookDetail';
import WishList from './components/books/WishList';
import WishDetail from './components/books/WishDetail';
import UserSettings from './components/settings/UserSettings';
import Navbar from './components/layout/Navbar';
import LandingPage from './components/landing/LandingPage';
import CollectionInsights from './components/insights/CollectionInsights';

const theme = createTheme({
  palette: {
    primary: {
      main: '#8B7355', // Warm brown (paper-like)
      light: '#A67C52',
      dark: '#6B4423',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#D2B48C', // Tan (light paper)
      light: '#E6D3B3',
      dark: '#B8860B',
      contrastText: '#212121',
    },
    background: {
      default: '#FDFBF7', // Very light cream
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2C1810', // Dark brown
      secondary: '#5D4037', // Medium brown
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(139, 115, 85, 0.15)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 12px rgba(139, 115, 85, 0.25)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.04)',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-2px)',
            transition: 'all 0.3s ease',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          backgroundColor: '#ffffff',
          color: '#212121',
        },
      },
    },
  },
});

function PrivateRoute({ children }) {
  const { currentUser } = useAuth();
  return currentUser ? children : <Navigate to="/login" />;
}

function AppContent() {
  const { currentUser } = useAuth();
  
  return (
    <Router>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {currentUser && <Navbar />}
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route 
            path="/books" 
            element={
              <PrivateRoute>
                <BookList />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/book/new" 
            element={
              <PrivateRoute>
                <BookDetail />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/book/:id" 
            element={
              <PrivateRoute>
                <BookDetail />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <PrivateRoute>
                <UserSettings />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/insights" 
            element={
              <PrivateRoute>
                <CollectionInsights />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/wishlist" 
            element={
              <PrivateRoute>
                <WishList />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/wish/:id" 
            element={
              <PrivateRoute>
                <WishDetail />
              </PrivateRoute>
            } 
          />
        </Routes>
      </ThemeProvider>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App; 